import { useEffect, useState } from 'react';
import { Alert, Button, CopyButton, Group, SegmentedControl, Stack, Text, Textarea } from '@mantine/core';
import { Dropzone, type FileWithPath } from '@mantine/dropzone';
import { CheckIcon, CopyIcon, FileArrowUpIcon, WarningIcon } from '@phosphor-icons/react';
import { decodeText, encodeText, fileToDataUrl } from '@/lib/base64';
import { useStrings } from '@/i18n/useLang';

function OutputBox({ value }: { value: string }) {
  const t = useStrings();
  if (!value) return null;
  return (
    <div>
      <Group justify="space-between" mb={4}>
        <Text fw={600} size="sm">
          {t.ui.output}
        </Text>
        <CopyButton value={value} timeout={1500}>
          {({ copied, copy }) => (
            <Button variant="subtle" size="xs" leftSection={copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />} onClick={copy}>
              {copied ? t.ui.copied : t.ui.copy}
            </Button>
          )}
        </CopyButton>
      </Group>
      <Textarea value={value} readOnly autosize minRows={2} maxRows={10} styles={{ input: { fontFamily: 'monospace' } }} />
    </div>
  );
}

export function Base64Tool() {
  const t = useStrings();
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fileUrl, setFileUrl] = useState('');

  useEffect(() => {
    if (!input) {
      setOutput('');
      setError(null);
      return;
    }
    try {
      setOutput(mode === 'encode' ? encodeText(input) : decodeText(input));
      setError(null);
    } catch {
      setOutput('');
      setError(t.ui.b64Error);
    }
  }, [input, mode, t.ui.b64Error]);

  return (
    <Stack gap="md">
      <SegmentedControl
        fullWidth
        value={mode}
        onChange={(v) => setMode(v as 'encode' | 'decode')}
        data={[
          { value: 'encode', label: t.ui.encode },
          { value: 'decode', label: t.ui.decode },
        ]}
      />
      <Textarea
        label={t.ui.input}
        placeholder={mode === 'encode' ? t.ui.plainPlaceholder : t.ui.b64Placeholder}
        value={input}
        onChange={(e) => setInput(e.currentTarget.value)}
        autosize
        minRows={3}
        maxRows={10}
        styles={{ input: { fontFamily: 'monospace' } }}
      />
      {error && (
        <Alert color="red" icon={<WarningIcon size={18} />} variant="light">
          {error}
        </Alert>
      )}
      <OutputBox value={output} />

      <div>
        <Text fw={500} size="sm" mb={4}>
          {t.ui.fileTitle}
        </Text>
        <Dropzone onDrop={(files: FileWithPath[]) => files[0] && fileToDataUrl(files[0]).then(setFileUrl)} multiple={false} radius="md">
          <Group justify="center" gap="sm" mih={70} style={{ pointerEvents: 'none' }}>
            <FileArrowUpIcon size={24} color="var(--primary)" />
            <Text size="sm" c="dimmed">
              {t.ui.fileHint}
            </Text>
          </Group>
        </Dropzone>
      </div>
      <OutputBox value={fileUrl} />
    </Stack>
  );
}
